import { Fragment } from 'react';
import { CopyId } from '@bb/ui';
import type { CampaignAnalytics, CampaignView, ScheduledDelivery } from '../../../api/client.ts';
import { formatWhen } from '../../segments/SegmentsListPage.tsx';
import type { ActionBasedDelivery, TriggerSpec } from '../trigger/actionBased.ts';
import { CHANNEL_LABELS, DELIVERY_LABELS } from '../labels.ts';
import { formatMetric, formatWindow } from './analytics.ts';

type Content = NonNullable<CampaignView['published']>['content'];

const TRIGGER_TEXT: Record<TriggerSpec['kind'], (t: TriggerSpec) => string> = {
  custom_event: (t) => {
    const e = t as Extract<TriggerSpec, { kind: 'custom_event' }>;
    const filters = e.propertyFilters?.length ?? 0;
    return `performs ${e.event || 'a custom event'}${filters ? ` (${filters} property ${filters === 1 ? 'filter' : 'filters'})` : ''}`;
  },
  session_start: () => 'starts a session',
  attribute_changed: (t) => `changes ${(t as Extract<TriggerSpec, { kind: 'attribute_changed' }>).attribute || 'an attribute'}`,
  subscription_changed: () => 'changes a subscription group state',
  message_interaction: () => 'interacts with another campaign',
};

function describeDelay(delay: ActionBasedDelivery['delay'] | undefined): string {
  if (!delay || delay.kind === 'none') return 'right away';
  if (delay.kind === 'after') return `after ${formatWindow(delay.seconds)}`;
  if (delay.kind === 'next_weekday') return `on the next ${['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][delay.weekday]} at ${delay.time}${delay.localTime ? " users' local time" : ''}`;
  return `${formatWindow(delay.days * 86_400)} later at ${delay.time}${delay.localTime ? " users' local time" : ''}`;
}

/** When the published revision sends, in one sentence. */
export function describeSchedule(deliveryType: CampaignView['deliveryType'], schedule: Content['schedule']): string {
  if (deliveryType === 'api_triggered') return 'When your backend calls the Campaign Trigger API.';
  if (deliveryType === 'action_based') {
    const ab = schedule.actionBased;
    if (!ab?.triggers.length) return 'Action-based, without triggers.';
    const cancel = ab.cancelOn?.length ? `; ${ab.cancelOn.length} cancel ${ab.cancelOn.length === 1 ? 'event' : 'events'}` : '';
    return `When the user ${ab.triggers.map((t) => TRIGGER_TEXT[t.kind](t)).join(' or ')}, ${describeDelay(ab.delay)}${ab.reevaluateAudienceAtSend ? ', audience checked again at send' : ''}${cancel}.`;
  }
  const s: ScheduledDelivery | undefined = schedule.scheduled;
  if (!s) return 'Scheduled, without a send time.';
  const local = 'localTime' in s && s.localTime ? " in users' local time" : '';
  if (s.kind === 'immediate') return 'Once, at launch.';
  if (s.kind === 'at') return `Once on ${s.date} at ${s.time}${local}.`;
  const every = s.interval > 1 ? `every ${s.interval} ${s.every}s` : `every ${s.every}`;
  return `${every.charAt(0).toUpperCase()}${every.slice(1)} at ${s.time}${local}, from ${s.startDate}${s.endDate ? ` to ${s.endDate}` : ''}.`;
}

function describeControls(controls: Content['deliveryControls']): string[] {
  const out: string[] = [];
  const re = controls.reeligibility;
  out.push(re?.enabled ? (re.windowSeconds ? `Users can receive it again ${formatWindow(re.windowSeconds)} after the last send.` : 'Users can receive it again right away.') : 'Each user receives it once.');
  if (controls.quietHours?.enabled) out.push(`Quiet hours ${controls.quietHours.from}–${controls.quietHours.to} in the user's time zone, then ${controls.quietHours.fallback === 'abort' ? 'the send is dropped' : 'it goes at the next allowed time'}.`);
  if (controls.ratePerMinute) out.push(`At most ${controls.ratePerMinute.toLocaleString('en-US')} sends per minute.`);
  return out;
}

/** The labels of the top-level include groups of an audience definition, when it has them. */
function audienceGroups(audience: unknown): string[] {
  const include = (audience as { include?: { label?: string; children?: Array<{ label?: string }> } } | null)?.include;
  if (!include) return [];
  return (include.children ?? [include]).flatMap((g) => (g.label ? [g.label] : []));
}

/**
 * The published revision's Delivery, Audience and Conversion settings, read-only (BB-104, as in Braze's campaign
 * analytics); editing them is the composer's job.
 */
export function SettingsSummary({ campaign, data }: { campaign: CampaignView; data: CampaignAnalytics }) {
  const published = campaign.published;
  if (!published) return null;
  const content = published.content;
  const groups = audienceGroups(content.audience);
  return (
    <section aria-label="Campaign settings" className="panel">
      <h3>Settings of revision {published.revision}</h3>
      <p className="hint">
        Published {formatWhen(published.publishedAt)}
        {published.publishedBy?.email ? ` by ${published.publishedBy.email}` : ''}. Read-only here; change them in the campaign.
      </p>
      <h4>Delivery</h4>
      <dl className="facts">
        <dt>Type</dt>
        <dd>
          {DELIVERY_LABELS[campaign.deliveryType]}, {campaign.category === 'transactional' ? 'transactional' : 'marketing'}
        </dd>
        <dt>Channels</dt>
        <dd>{campaign.channels.map((ch) => CHANNEL_LABELS[ch]).join(', ')}</dd>
        <dt>Sends</dt>
        <dd>{describeSchedule(campaign.deliveryType, content.schedule)}</dd>
        {(campaign.startAt ?? content.schedule.startAt) && (
          <>
            <dt>Starts</dt>
            <dd>{formatWhen((campaign.startAt ?? content.schedule.startAt)!)}</dd>
          </>
        )}
        {(campaign.endAt ?? content.schedule.endAt) && (
          <>
            <dt>Stop date</dt>
            <dd>{formatWhen((campaign.endAt ?? content.schedule.endAt)!)}</dd>
          </>
        )}
        <dt>Delivery controls</dt>
        <dd>
          <ul className="plain-list">
            {describeControls(content.deliveryControls).map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </dd>
      </dl>
      <h4>Audience</h4>
      <p>{content.audience ? (groups.length ? `Users in: ${groups.join(', ')}.` : 'A custom audience.') : 'Everyone in the environment.'}</p>
      <h4>Conversions</h4>
      <ul className="plain-list">
        {data.conversions.map((c) => (
          <li key={c.id}>
            {c.kind === 'session_start' ? 'Starts a session' : `Performs ${c.event ?? 'a custom event'}`} within {formatWindow(c.windowSeconds)}
            {c.primary ? ' (primary)' : ''}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** API ids of the campaign, its variants and control, each with a Copy button (BB-104). */
export function ApiIdsPanel({ data }: { data: CampaignAnalytics }) {
  return (
    <section aria-label="API identifiers" className="panel">
      <h3>API identifiers</h3>
      <dl className="facts">
        <dt>Campaign</dt>
        <dd>
          <CopyId label="campaign API id" value={data.apiIds.campaign} />
        </dd>
        {data.apiIds.variants.map((v) => (
          <Fragment key={v.apiId}>
            <dt>{v.name}</dt>
            <dd>
              <CopyId label={`API id of ${v.name}`} value={v.apiId} />
            </dd>
          </Fragment>
        ))}
        {data.apiIds.control && (
          <>
            <dt>Control</dt>
            <dd>
              <CopyId label="control API id" value={data.apiIds.control} />
            </dd>
          </>
        )}
      </dl>
    </section>
  );
}

/**
 * The control group apart from the KPIs (as in Braze) and every conversion of the published revision with its result
 * (BB-104, BB-103): conversions are distinct users who converted within the window of their send, control excluded.
 */
export function ConversionsPanel({ data }: { data: CampaignAnalytics }) {
  return (
    <section aria-label="Conversions and control" className="panel">
      <h3>Conversions</h3>
      <table className="table" aria-label="Conversion events">
        <thead>
          <tr>
            <th scope="col">Conversion</th>
            <th scope="col">Window</th>
            <th scope="col">Converted</th>
            <th scope="col">Rate</th>
          </tr>
        </thead>
        <tbody>
          {data.conversions.map((c) => (
            <tr key={c.id}>
              <th scope="row">
                {c.kind === 'session_start' ? 'Session start' : (c.event ?? 'Custom event')}
                {c.primary ? ' · primary' : ''}
              </th>
              <td>{formatWindow(c.windowSeconds)}</td>
              <td>{c.converted.toLocaleString('en-US')}</td>
              <td>{formatMetric('percent', c.rate)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="hint">Rate = users who converted within the window of their send ÷ users whose window opened in the period. Control is not included.</p>
      <h3>Control group</h3>
      {data.control ? (
        <dl className="facts" aria-label="Control group">
          <dt>Assigned</dt>
          <dd>{data.control.assigned.toLocaleString('en-US')}</dd>
          <dt>Conversion windows</dt>
          <dd>{data.control.conversionRecipients.toLocaleString('en-US')}</dd>
          <dt>Converted (primary)</dt>
          <dd>{data.control.conversions.toLocaleString('en-US')}</dd>
          <dt>Conversion rate</dt>
          <dd>{formatMetric('percent', data.control.conversionRate)}</dd>
        </dl>
      ) : (
        <p className="hint">This campaign has no control group.</p>
      )}
      {data.control && <p className="hint">Control users get no message; their window opens at assignment. The «Variants and control» tab compares each variant with control statistically.</p>}
    </section>
  );
}
